/*
 * Estilo do bloco do app Shopify Forms no rodape.
 *
 * O app desenha o formulario dentro de um shadow DOM, entao o CSS do tema nao
 * alcanca ele. Duas frentes cobrem isso:
 *
 * 1. Variaveis CSS (cores, fontes, raios, paddings) — declaradas no CSS do tema
 *    sobre o .bs-footer__app-form. Variaveis atravessam a fronteira do shadow,
 *    entao essa parte e CSS puro e nao depende de nada daqui.
 *
 * 2. O que nao tem variavel (esconder o cabecalho e o disclaimer que o app
 *    repete, tirar o seletor de pais, tirar o label de dentro do campo) precisa
 *    de uma folha de estilo dentro do shadow root. Como o shadow e aberto, da
 *    para acrescentar uma folha adotada — API padrao, sem depender de nenhum
 *    gancho interno do app.
 *
 * Os seletores usados sao os estaveis do app: data-testid, id e a classe
 * .phone-country-selector, que nao tem hash. As classes com hash
 * (_formHeader_jnbzb_24 e afins) mudam a cada build do app e ficaram de fora de
 * proposito — no lugar delas vao relacoes estruturais (:has, irmao adjacente).
 */
(() => {
  const SHEET_ID = "bs-forms-app";

  const CSS = `
    /* o desenho nao tem seletor de pais: o campo ocupa a linha inteira.
       Esconder so o seletor nao basta — a linha dele e um flex container que
       continuava reservando a coluna, deixando o campo estreito e o label
       quebrando em duas linhas. */
    .phone-country-selector {
      display: none !important;
    }
    div:has(> .phone-country-selector) {
      display: block !important;
      width: 100% !important;
    }

    /* O app monta um grid de "imagem + conteudo" e da uma largura de coluna ao
       conteudo. Sem imagem, no mobile isso deixava a secao com 154px dentro de
       um bloco de 327. Aqui a arvore inteira vira bloco de largura cheia. */
    #app-embed,
    #app-embed [data-testid="app-router"],
    #form-container-ref,
    #form-container-ref section,
    #form-container-ref > section > section {
      display: block !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      margin-inline: 0 !important;
      padding-inline: 0 !important;
    }

    /* o form vinha 4px mais largo que a caixa (padding lateral somado por fora,
       em content-box), o que empurrava o botao para fora do rodape */
    form[data-testid="form"],
    form[data-testid="form"] *,
    form[data-testid="form"] *::before,
    form[data-testid="form"] *::after {
      box-sizing: border-box !important;
    }
    form[data-testid="form"] {
      display: flex !important;
      flex-direction: column !important;
      gap: 16px !important;
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    /* campo 48:629 — label acima da linha, nao flutuando dentro dela */
    [data-testid="field-phone_number"] {
      width: 100% !important;
      height: 36px !important;
      min-height: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-bottom: 1px solid var(--bs-form-line, #ccc) !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      outline: 0 !important;
      font-family: var(--bs-form-font, inherit) !important;
      font-size: 13px !important;
      font-weight: 400 !important;
      letter-spacing: -0.2px !important;
      color: var(--bs-form-ink, #444) !important;
      transition: border-color var(--bs-form-duration, 0.25s) ease !important;
    }
    [data-testid="field-phone_number"]:focus {
      border-bottom-color: var(--bs-form-ink, #444) !important;
      box-shadow: none !important;
      outline: 0 !important;
    }

    /* o container do campo vira a referencia do label, que sai de dentro da
       linha e sobe para cima dela */
    div:has(> [data-testid="field-phone_number"]) {
      position: relative !important;
      width: 100% !important;
      padding-top: 18px !important;
    }
    #label-phone_number {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      transform: none !important;
      padding: 0 !important;
      font-family: var(--bs-form-font, inherit) !important;
      font-size: 12px !important;
      font-weight: 400 !important;
      line-height: normal !important;
      letter-spacing: 0 !important;
      color: var(--bs-form-eyebrow, #777) !important;
    }

    /* botao 48:632 — 40px, borda hairline, raio 3, fundo transparente.
       O app deixa o botao como flex sem align-items, entao o texto colava no
       topo da caixa de 40px em vez de centralizar. */
    [data-testid="btn-form-submit"] {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 40px !important;
      min-height: 0 !important;
      padding: 0 !important;
      border: 1px solid var(--bs-form-hairline, #e8e8e8) !important;
      border-radius: 3px !important;
      background: transparent !important;
      box-shadow: none !important;
      font-family: var(--bs-form-font, inherit) !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
      /* em repouso, o mesmo cinza e a mesma borda hairline do botao original do
         rodape; no hover ele preenche igual ao .m-button--primary do tema */
      color: var(--bs-form-muted, #666) !important;
      cursor: pointer !important;
      transition:
        color var(--bs-form-duration, 0.25s) ease,
        background-color var(--bs-form-duration, 0.25s) ease,
        border-color var(--bs-form-duration, 0.25s) ease !important;
    }
    [data-testid="btn-form-submit"]:hover,
    [data-testid="btn-form-submit"]:focus-visible {
      background: var(--bs-form-btn-hover-bg, #454545) !important;
      border-color: var(--bs-form-btn-hover-bg, #454545) !important;
      color: var(--bs-form-btn-hover-ink, #fff) !important;
    }

    /* O app repete titulo, descricao e disclaimer que o rodape ja imprime por
       fora do bloco; sem esconder, tudo aparece duas vezes.
       Estas regras ficam no fim e ancoradas no #form-container-ref de proposito:
       as regras de largura acima usam esse id e, sem a mesma ancora, venciam o
       display:none por especificidade e o cabecalho voltava a aparecer. */
    #form-container-ref section:has(> h2) {
      display: none !important;
    }
    #form-container-ref form[data-testid="form"] ~ div {
      display: none !important;
    }
  `;

  const apply = (host) => {
    const root = host.shadowRoot;
    if (!root) return false;
    // o shadow precisa estar fechado para leitura? nao: so segue se abriu
    if (root[SHEET_ID]) return true;
    if (!("adoptedStyleSheets" in root) || typeof CSSStyleSheet === "undefined") return false;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      root[SHEET_ID] = sheet;
      return true;
    } catch (e) {
      // uma folha a menos nao pode derrubar o rodape: o formulario continua
      // funcionando com a aparencia padrao do app
      console.warn("[bs-forms-app] nao foi possivel aplicar o estilo:", e);
      return false;
    }
  };

  const scan = () => {
    document
      .querySelectorAll(".bs-footer__app-form shopify-forms-embed")
      .forEach(apply);
  };

  scan();
  // o app monta o embed depois do parse e volta a montar ao trocar de etapa
  // (formulario -> sucesso), entao a folha e reaplicada enquanto o rodape viver
  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
